import { useEffect, useMemo, useState } from "react";
import { AddOnTextInput, Button, CheckboxInput, Icon, IconButton, Skeleton, Toggle, Tooltip, Typography } from "@/components/atoms";
import { DataTable } from "@/components/molecules";
import { FixedBaseInterval, FixedIntervals, FixedPricingState, PricingProfile } from "@/redux/slices/trip/types";
import { generateEditColumns, getManagementCostUnitSymbol, getTotalTripCost, validateRanges } from "./helpers";
import { useNavigate } from "react-router-dom";
import { addToast, getCurrencySymbol } from "@/utils";
import { useDeleteStateBasedFixedPricingMutation, useUpdateStateBasedFixedPricingMutation } from "@/redux/apis/config/trip/tripCost";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePrompt } from "@/hooks";
import { ManagementCostUnit, StateRatesEditAction, StateRatesEditTableData } from "./types";
import { StateRatesFormData, stateRatesFormSchema } from "./stateRatesFormSchema";
import { StateRatesModals } from "./StateRatesModals";

interface StateRatesEditPanelProps {
  state: FixedPricingState;
  pricingProfile: PricingProfile;
}

export const StateRatesEditPanel = ({ state, pricingProfile }: StateRatesEditPanelProps) => {
  const navigate = useNavigate();
  const { name, baseInterval, intervals, currency, distanceUnit } = state;
  const currencySymbol = getCurrencySymbol(currency) || "";
  const [managementCostUnit, setManagementCostUnit] = useState<ManagementCostUnit>(
    (baseInterval?.managementCostUnit as ManagementCostUnit) ?? "percentage"
  );
  const [selectedRange, setSelectedRange] = useState<number | null>(null);
  const [action, setAction] = useState<StateRatesEditAction>(undefined);
  const [isOpenEnded, setIsOpenEnded] = useState(false);

  const [updateStateRates, { isLoading: isLoadingUpdate }] = useUpdateStateBasedFixedPricingMutation();
  const [deleteStateRates, { isLoading: isLoadingDelete }] = useDeleteStateBasedFixedPricingMutation();
  const isLoading = isLoadingUpdate || isLoadingDelete;

  const {
    register,
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, errors, isSubmitted },
  } = useForm<StateRatesFormData>({
    defaultValues: {
      baseInterval: {
        minimumRange: 0,
        maximumRange: baseInterval?.maximumRange || 0,
        intervalCost: baseInterval?.intervalCost || 0,
        managementCost: baseInterval?.managementCost || 0,
        managementCostUnit,
      },
      intervals: intervals.map((interval) => ({
        maximumRange: interval.maximumRange || 0,
        intervalCost: interval.intervalCost || 0,
        managementCost: interval.managementCost || 0,
        managementCostUnit,
      })),
    },
    resolver: zodResolver(stateRatesFormSchema),
  });
  const { append, remove } = useFieldArray({
    control,
    name: "intervals",
  });
  const values = watch();

  const promptWhen = isDirty  && !isSubmitted;
  usePrompt({ when: promptWhen, message: "Changes will not be saved. Do you want to proceed?" });

  useEffect(() => {
    const oldManagementCostUnit = values.baseInterval.managementCostUnit;
    if (managementCostUnit !== oldManagementCostUnit) {
      const oldBaseIntervalManagementCost = values.baseInterval.managementCost;
      const baseIntervalIntervalCost = values.baseInterval.intervalCost;
      let newBaseIntervalManagementCost = 0;
      if (baseIntervalIntervalCost > 0) {
        newBaseIntervalManagementCost =
          managementCostUnit === "percentage"
            ? Number(((oldBaseIntervalManagementCost / values.baseInterval.intervalCost) * 100).toFixed())
            : Number(((oldBaseIntervalManagementCost / 100) * values.baseInterval.intervalCost).toFixed(2));
      }
      setValue("baseInterval.managementCost", newBaseIntervalManagementCost, { shouldDirty: true });
      setValue("baseInterval.managementCostUnit", managementCostUnit, { shouldDirty: true });

      for (let i = 0; i < values.intervals.length; i++) {
        const oldIntervalManagementCost = values.intervals[i].managementCost;
        const intervalIntervalCost = values.intervals[i].intervalCost;
        let newIntervalManagementCost = 0;
        if (intervalIntervalCost > 0) {
          newIntervalManagementCost =
            managementCostUnit === "percentage"
              ? Number(((oldIntervalManagementCost / values.intervals[i].intervalCost) * 100).toFixed())
              : Number(((oldIntervalManagementCost / 100) * values.intervals[i].intervalCost).toFixed(2));
        }
        setValue(`intervals.${i}.managementCost`, newIntervalManagementCost, { shouldDirty: true });
        setValue(`intervals.${i}.managementCostUnit`, managementCostUnit, { shouldDirty: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementCostUnit]);

  const onSubmit = handleSubmit((data) => {
    const isValid = validateRanges([
      data.baseInterval.minimumRange,
      data.baseInterval.maximumRange,
      ...data.intervals.map((interval) => interval.maximumRange),
    ]);
    if (isValid) {
      updateStateRates({
        id: pricingProfile.id,
        state: state.state,
        base_interval: {
          range_from: data.baseInterval.minimumRange,
          range_to: data.baseInterval.maximumRange,
          interval_cost: data.baseInterval.intervalCost * 100,
          management_cost: managementCostUnit === "percentage" ? data.baseInterval.managementCost : data.baseInterval.managementCost * 100,
          management_cost_unit: data.baseInterval.managementCostUnit,
          note: "",
        },
        intervals: data.intervals.map((i) => ({
          range_to: i.maximumRange,
          interval_cost: i.intervalCost * 100,
          management_cost: managementCostUnit === "percentage" ? i.managementCost : i.managementCost * 100,
          management_cost_unit: i.managementCostUnit,
          note: "",
        })),
      })
        .unwrap()
        .then(() => {
          addToast("success", "Successfully Updated State Rates");
          navigate("..");
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    }
  });

  const handleResetAndDeleteSettings = () => {
    setAction(undefined);
    deleteStateRates({ id: pricingProfile.id, state: state.state })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Reset and Deleted Settings");
        navigate("..");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleAddRange = () => {
    append({
      maximumRange:
        values.intervals.length > 0
          ? Number(values.intervals[values.intervals.length - 1].maximumRange) + 1
          : Number(values.baseInterval.maximumRange) + 1,
      intervalCost: 0,
      managementCost: values.baseInterval.managementCost,
      managementCostUnit,
    });
  };

  const handleDeleteRange = (i: number) => {
    setSelectedRange(i);
    setAction("deleteRange");
  };

  const handleConfirmDeleteRange = () => {
    if (selectedRange != null) {
      remove(selectedRange-2);
      setAction(undefined);
    }
  };

  const handleManagementCostUnitToggle = (checked: boolean) => {
    setManagementCostUnit(checked ? "percentage" : "fixed");
    addToast("success", `Management Fee switched to ${checked ? "Percentage" : "Fixed Dollar Value"}`, 5000, { replace: true });
  };

  const generateEditData = (baseInterval: FixedBaseInterval, intervals: FixedIntervals) => {
    const jointIntervals = baseInterval ? [baseInterval, ...intervals] : [];
    const data = jointIntervals.map((_, i) => {
      return {
        rangeIntervals: `Range ${i+1}`,
        range: i > 0 ? (
          <div className="flex flex-col gap-x-2 gap-y-2 md:flex-row lg:flex-row">
            <div className="flex flex-row items-center gap-x-2">
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                value={i > 1 ? Number(intervals[i - 2].maximumRange) : Number(baseInterval?.maximumRange)}
                disabled
              />
              <Typography variant="small" className="text-neutral-dark-gray">
                -
              </Typography>
            </div>
            {i === jointIntervals.length - 1 ? (
              <>
                <AddOnTextInput
                  endAddOn={distanceUnit}
                  type="number"
                  size="sm"
                  className="w-[110px] [&_input]:text-right"
                  hasError={!!errors.intervals?.[i-1]?.maximumRange}
                  step="any"
                  {...register(`intervals.${i-1}.maximumRange`)}
                />
                <CheckboxInput 
                  size="md"
                  variant="check"
                  label="Set as open ended" 
                  checked={isOpenEnded} 
                  onChange={(e) => setIsOpenEnded(e.target.checked)}
                  disabled
                />
              </>
            ) : (
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                hasError={!!errors.intervals?.[i-1]?.maximumRange}
                step="any"
                {...register(`intervals.${i-1}.maximumRange`)}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2 md:flex-row lg:flex-row">
            <div className="flex flex-row items-center gap-x-2">
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                value={0}
                disabled
              />
              <Typography variant="paragraph">-</Typography>
            </div>
            <AddOnTextInput
              endAddOn={distanceUnit}
              type="number"
              size="sm"
              className="w-[110px] [&_input]:text-right"
              hasError={!!errors.baseInterval?.maximumRange}
              {...register("baseInterval.maximumRange")}
            />
          </div>
        ),
        baseTripCost: i > 0 ? (
          <AddOnTextInput
            startAddOn={`${currency} ${currencySymbol}`}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.intervals?.[i-1]?.intervalCost}
            step="any"
            {...register(`intervals.${i-1}.intervalCost`)}
          />
        ) : (
          <AddOnTextInput
            startAddOn={`${currency} ${currencySymbol}`}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.baseInterval?.intervalCost}
            step="any"
            {...register("baseInterval.intervalCost")}
          />
        ),
        managementFee: i > 0 ? (
          <AddOnTextInput
            endAddOn={getManagementCostUnitSymbol(currencySymbol, intervals[i-1].managementCostUnit)}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.intervals?.[i-1]?.managementCost}
            step="any"
            {...register(`intervals.${i-1}.managementCost`)}
          />
        ) : (
          <AddOnTextInput
            endAddOn={getManagementCostUnitSymbol(currencySymbol, baseInterval?.managementCostUnit)}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.baseInterval?.managementCost}
            step="any"
            {...register("baseInterval.managementCost")}
          />
        ),
        totalTripCost: i > 0 ? getTotalTripCost(
            Number(intervals[i-1]?.intervalCost),
            Number(intervals[i-1]?.managementCost),
            currency,
            intervals[i-1]?.managementCostUnit
          ) : getTotalTripCost(
            Number(baseInterval?.intervalCost),
            Number(baseInterval?.managementCost),
            currency,
            baseInterval?.managementCostUnit
          ),
        action: i > 1 && (
          <div className="flex items-center justify-center">
            <IconButton variant="custom" iconName="Trash" className="text-danger" iconSize="md" onClick={() => handleDeleteRange(i+1)} />
          </div>
        ),
      } as StateRatesEditTableData;
    });
    return data;
  };

  const data = useMemo(
    () => generateEditData(values.baseInterval as FixedBaseInterval, values.intervals as FixedIntervals),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );

  const columns = useMemo(
    () => generateEditColumns(distanceUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (isLoading) return <Skeleton />;

  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <div className="flex flex-col xl:flex-row justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{name}</Typography>
          <Typography variant="paragraph" className="whitespace-pre-line text-neutral-dark-gray">{`Set up kilometer-based pricing. Add different rates for different distances.\n For example, ${currencySymbol}5/${distanceUnit} for trips under 10${distanceUnit} and ${currencySymbol}10/${distanceUnit} for longer trips.`}</Typography>
        </div>
        <div className="flex flex-row items-center gap-x-3">
          <div className="flex flex-row items-center gap-x-1.5">
            <div className="flex flex-row items-center gap-x-1.5">
              <Typography variant="action" className="text-neutral-dark-gray">Management Fee ({managementCostUnit === "percentage" ? "%" : currencySymbol})</Typography>
              <Tooltip
                content={`Switch between setting your Management Fee as a percentage (%) or a fixed dollar amount (${currencySymbol}).`}
                placement="bottom"
              >
                <span>
                  <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
                </span>
              </Tooltip>
            </div>
            <Toggle
              checked={managementCostUnit === "percentage"}
              onChange={handleManagementCostUnitToggle}
              className={managementCostUnit === "percentage" ? "bg-info" : "!bg-success"}
              size="sm"
            >
              {managementCostUnit === "percentage" ? (
                <Typography variant="paragraph" className="text-info">
                  %
                </Typography>
              ) : (
                <Typography variant="paragraph" className="text-success">
                  {currencySymbol}
                </Typography>
              )}
            </Toggle>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate("..")}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" disabled={!isDirty}>Save</Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data}
        className="[&_table]:table-auto overflow-x-auto w-full bg-white rounded-b-none mt-4"
      />
      <div className="flex w-full items-center justify-between ring-1 ring-neutral-mid-gray rounded-b-lg bg-white p-3">
        <Button variant="secondary" size="sm" startIcon="Add" onClick={handleAddRange}>Add Range</Button>
      </div>
      <div className="flex items-center justify-center mt-6">
        <Button variant="secondary" size="sm" startIcon="Danger" iconVariant="Bold" iconClassName="text-warning" onClick={() => setAction("resetAndDeleteSettings")} disabled={!baseInterval}>Reset and Delete Settings</Button>
      </div>
      <StateRatesModals
        action={action}
        selectedRange={selectedRange}
        onDeleteRange={() => handleConfirmDeleteRange()}
        onResetAndDeleteSettings={() => handleResetAndDeleteSettings()}
        onClose={() => setAction(undefined)}
      />
    </form>
  );
};
